import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosServices from '../../services/axios';

// --- ASYNC THUNKS ---

/**
 * Fetches all assets for a given group from the backend.
 */
export const fetchAssets = createAsyncThunk(
    'library/fetchAssets',
    async (params, { rejectWithValue }) => {
        const normalizedParams =
            typeof params === 'object' && params !== null
                ? params
                : { groupId: params };

        const {
          groupId,
          page = 1,
          limit = 24,
          search,
          status,
          type,
          sort = 'createdAt:desc',
          append = false,
          } = normalizedParams;

        if (!groupId) {
            return {
                assets: [],
                totalSize: 0,
                pagination: { page: 1, limit, total: 0, pages: 0 },
            };
        }

        try {
            const response = await axiosServices.get('/library/assets', {
                params: {
                    groupId,
                    page,
                    limit,
                    search,
                    status,
                    type,
                    sort,
                },
            });
            return {
...response.data,
append,
};
        } catch (error) {
            const errorMsg = error.response?.data?.message || 'Failed to fetch assets';
            console.error(`[fetchAssets] Error for groupId ${groupId}:`, errorMsg);
            return rejectWithValue(errorMsg);
        }
    }
);

/**
 * Deletes a single asset from the backend.
 */
export const deleteAsset = createAsyncThunk(
  'library/deleteAsset',
  async ({ assetUrl, groupId }, { rejectWithValue, getState }) => {
    try {
      console.log(`[deleteAsset] Deleting single asset: ${assetUrl} from groupId: ${groupId}`);
      const { library } = getState();
      const assetToDelete = library.assets.find(asset => asset.url === assetUrl);
      
      if (!assetToDelete) {
        throw new Error("Asset not found in current state.");
      }

      await axiosServices.post(`/library/delete-asset?groupId=${groupId}`, { url: assetUrl });
      return assetToDelete; // Return the asset object for local state update
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Failed to delete asset';
      console.error(`[deleteAsset] Error for asset ${assetUrl}:`, errorMsg);
      return rejectWithValue(errorMsg);
    }
  }
);

/**
 * Deletes multiple assets in a single request.
 */
export const deleteMultipleAssets = createAsyncThunk(
  'library/deleteMultipleAssets',
  async ({ urls, groupId }, { rejectWithValue }) => {
    try {
      console.log(`[deleteMultipleAssets] Deleting ${urls.length} assets from groupId: ${groupId}`);
      await axiosServices.post(`/library/delete-multiple-assets?groupId=${groupId}`, { urls });
      return urls; // Return the array of deleted URLs to confirm success
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Failed to delete assets';
      console.error(`[deleteMultipleAssets] Error:`, errorMsg);
      return rejectWithValue(errorMsg);
    }
  }
);

/**
 * Renames a single asset on the backend.
 */
export const renameAsset = createAsyncThunk(
  'library/renameAsset',
  async ({ oldUrl, newName, groupId }, { rejectWithValue }) => {
    try {
      console.log(`[renameAsset] Renaming ${oldUrl} to ${newName} in groupId: ${groupId}`);
      const response = await axiosServices.post(`/library/rename-asset?groupId=${groupId}`, { oldUrl, newName });
      return { oldUrl, newAsset: response.data.newAsset };
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Failed to rename asset';
      console.error(`[renameAsset] Error renaming ${oldUrl}:`, errorMsg);
      return rejectWithValue(errorMsg);
    }
  }
);

// --- SLICE DEFINITION ---

const initialState = {
    assets: [],
    status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
    error: null,
    totalSize: 0,
    pagination: {
      page: 1,
      limit: 50,
      total: 0,
      pages: 0,
      },
};

const librarySlice = createSlice({
  name: 'library',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Reducers for fetchAssets
      .addCase(fetchAssets.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchAssets.fulfilled, (state, action) => {
state.status = 'succeeded';

const incomingAssets = action.payload.assets || [];
const combinedAssets = action.payload.append
? [...state.assets, ...incomingAssets]
: incomingAssets;

const seenUrls = new Set();
const uniqueAssets = combinedAssets.filter(asset => {
if (!asset?.url || seenUrls.has(asset.url)) {
return false;
}
seenUrls.add(asset.url);
return true;
});

state.assets = uniqueAssets;
state.totalSize = action.payload.totalSize || 0;
state.pagination = action.payload.pagination || {
page: 1,
limit: uniqueAssets.length,
total: uniqueAssets.length,
pages: 1,
};
})
      .addCase(fetchAssets.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      // Reducers for deleteAsset (Single)
      .addCase(deleteAsset.fulfilled, (state, action) => {
        const deletedAsset = action.payload;
        state.assets = state.assets.filter((asset) => asset.url !== deletedAsset.url);
        state.totalSize -= (deletedAsset.size || 0);
        if (state.totalSize < 0) state.totalSize = 0;
      })

      // Reducers for deleteMultipleAssets (Bulk)
      .addCase(deleteMultipleAssets.fulfilled, (state, action) => {
        console.log(`[Reducer] Bulk delete of ${action.payload.length} assets fulfilled.`);
      })

      // Reducers for renameAsset
      .addCase(renameAsset.fulfilled, (state, action) => {
          const index = state.assets.findIndex(asset => asset.url === action.payload.oldUrl);
          if (index !== -1) {
              state.assets[index] = action.payload.newAsset;
          }
      });
  },
});

export default librarySlice.reducer;